import { stringRoutes } from "@/config/global.strings";
import { groupBy } from "@/helpers/groupByHelper";
import { WPContent } from "@/models/Content";

import {
  DirectionEnum,
  ExtraTypeEnum,
  ModeOfTravelEnum,
  TermTypeEnum,
} from "@/models/enums";
import { Extra } from "@/models/www/Extra";
import {
  ExtraInnerSection,
  ExtrasComponent,
} from "@/models/www/ExtrasComponent";
import { ResourceState } from "@/models/www/store/BookingState";
import contentService from "@/services/content.service";
import extrasService from "@/services/extras.service";
import { useBookingStore } from "@/store/booking";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import {getUnixTime, isAfter} from "date-fns";

const extrasComponentsRef = ref<ExtrasComponent[]>([]);
export const extrasComponents = computed(() => extrasComponentsRef.value);

const useExtras = () => {
  const { t } = useI18n();
  const bookingStore = useBookingStore();
  const modeOfTravel = computed(() => {
    return bookingStore.booking.modeOfTravel;
  });

  const setExtraComponentChecked = (index: number, checked: boolean) => {
    const components = extrasComponents.value;
    components[index].checked = checked;
    extrasComponentsRef.value = components;
  };

  const updateStateResources = (extras: Extra[]) => {
    [DirectionEnum.Outward, DirectionEnum.Return].forEach((d) => {
      const updatedResources = [] as ResourceState[];
      const resources = bookingStore.getResources(d);

      resources.forEach((resource) => {
        let identifier = "";
        if (resource.code === "BUS") {
          identifier =
            extras.find((e) => e.resourceCode === resource.code &&
              e.route === resource.route
            )?.identifier ?? "";
        } else {
          identifier =
            extras.find((e) => e.resourceCode === resource.code
            )?.identifier ?? "";
        }
        updatedResources.push({ ...resource, identifier: identifier });
      });

      bookingStore.updateResources(d, updatedResources);
    });
  };

  const getExtras = () => {
    const extrasDirectionEnum = modeOfTravel.value === ModeOfTravelEnum.SailAndFly ? DirectionEnum.Return : DirectionEnum.Outward;

    const promises: Promise<unknown>[] = [
      extrasService.getExtras(),
      contentService.getContent(
        "extras",
        modeOfTravel.value,
        bookingStore.getRoute(extrasDirectionEnum)
      ),
    ];
    return new Promise((resolve, reject) => {
      Promise.all(promises)
        .then((resp) => {
          updateStateResources(resp[0] as Extra[]);
          resolve(
            getComponents((resp[0] as Extra[]) ?? [], resp[1] as WPContent[])
          );
        })
        .catch((err) => reject(err));
    });
  };

  const getComponents = (extras: Extra[], content: WPContent[]) => {

    const extrasDirectionEnum = modeOfTravel.value === ModeOfTravelEnum.SailAndFly ? DirectionEnum.Return : DirectionEnum.Outward;
    const components = [] as ExtrasComponent[];
    const groupedExtras = groupBy(extras, "type");
    const currentResources = bookingStore.getResources(extrasDirectionEnum);
    const filteredContent = filterSectionOnValidToDate(content);

    const hasResourceInGroup = (key: string) => {
      const resourcesForGroup = groupedExtras[key] as Extra[];
      return currentResources.some((r) =>
        resourcesForGroup.map((rg) => rg.resourceCode).includes(r.code)
      );
    };

    for (const key in groupedExtras) {
      const contentForExtra = filteredContent.find((c) => c.id === key);

      switch (key) {
        case ExtraTypeEnum[ExtraTypeEnum.Parking]: {
          const innerSections = [] as ExtraInnerSection[];

          contentForExtra?.sections.forEach((section, index) => {
              innerSections.push({
                content: section.body,
                resources: (groupedExtras[key] as Extra[]).filter((_, i) => i === index),
              });
        });

        components.push({
            expanded: hasResourceInGroup(key),
            componentName: "BookingExtraCheckboxList",
            componentType: ExtraTypeEnum.Parking,
            title: contentForExtra?.title ?? "",
            description: contentForExtra?.description ?? "",
            preview: contentForExtra?.preview ?? "",
            content: contentForExtra?.body ?? "",
            innerSections: innerSections,
            icon: "extras/parking",
            order: 0,
          });

          break;
        }

        case ExtraTypeEnum[ExtraTypeEnum.Wheelchair]:
          components.push({
            expanded: hasResourceInGroup(key),
            componentName: "BookingExtraQuantityList",
            title: contentForExtra?.title ?? "",
            description: contentForExtra?.description ?? "",
            preview: contentForExtra?.preview ?? "",
            content: contentForExtra?.body ?? "",
            innerSections:
              bookingStore.getBooking.modeOfTravel ===
                ModeOfTravelEnum.Scillonian
                ? [
                  {
                    content: `<p><strong>Pre-book wheelchair</strong></p>`,
                    resources: groupedExtras[key],
                  },
                ]
                : [],
            icon: "extras/wheelchair",
            order: 1,
          });

          break;
        case ExtraTypeEnum[ExtraTypeEnum.ExcessLuggage]: {

          const footerForExtra = filteredContent.find((c) => c.id === 'ExcessLuggageFooter');

          components.push({
            expanded: hasResourceInGroup(key),
            componentName: "BookingExtraQuantityList",
            title: contentForExtra?.title ?? "",
            description: contentForExtra?.description ?? "",
            preview: contentForExtra?.preview ?? "",
            content: contentForExtra?.body ?? "",
            innerSections: [
              {
                resources: groupedExtras[key],
                footer: footerForExtra?.body
              },
            ],
            icon: "extras/bag",
            order: 5,
          });

          break;
        }

        case ExtraTypeEnum[ExtraTypeEnum.Bus]: {
          const innerSections = [] as ExtraInnerSection[];

          contentForExtra?.sections.forEach((section, index) => {
            const route = bookingStore.getRoute(
              index === 0 ? extrasDirectionEnum : extrasDirectionEnum === DirectionEnum.Return ? DirectionEnum.Outward : DirectionEnum.Return
            );
            innerSections.push({
              content: section.body,
              resources: (groupedExtras[key] as Extra[]).filter(
                (x) => x.route === route
              ),
            });
          });
          components.push({
            expanded: hasResourceInGroup(key),
            componentName: "BookingExtraCheckboxList",
            title: contentForExtra?.title ?? "",
            description: contentForExtra?.description ?? "",
            preview: contentForExtra?.preview ?? "",
            content: contentForExtra?.body ?? "",
            innerSections: innerSections,
            icon: "extras/bus",
            order: 2,
          });

          break;
        }
      }
    }

    const contentForExtraLuggage = filteredContent.find(
      (c) => c.id === ExtraTypeEnum[ExtraTypeEnum.Luggage]
    );
    components.push({
      expanded: false,
      title: contentForExtraLuggage?.title ?? "",
      description: contentForExtraLuggage?.description ?? "",
      preview: contentForExtraLuggage?.preview ?? "",
      content: contentForExtraLuggage?.body ?? "",
      innerSections:
        contentForExtraLuggage?.sections.map((c) => ({ content: c.body })) ??
        [],
      icon: "extras/bag",
      order: 3,
    });

    //don't display pets section if exeter route
    const addPetsInfo = [DirectionEnum.Outward, DirectionEnum.Return].map(
      (d) =>
        [stringRoutes.ISCEXT, stringRoutes.EXTISC].includes(
          bookingStore.getRoute(d)
        ) == false
    );

    if (addPetsInfo.every((x) => x === true)) {
      const contentForExtraPets = filteredContent.find(
        (c) => c.id === ExtraTypeEnum[ExtraTypeEnum.Pets]
      );
      components.push({
        expanded: false,
        title: contentForExtraPets?.title ?? "",
        description: contentForExtraPets?.description ?? "",
        preview: contentForExtraPets?.preview ?? "",
        content: contentForExtraPets?.body ?? "",
        innerSections:
          contentForExtraPets?.sections.map((c) => ({ content: c.body })) ?? [],
        icon: "extras/dog",
        order: 4,
      });
    }

    if ([ModeOfTravelEnum.Skybus, ModeOfTravelEnum.FlyAndSail, ModeOfTravelEnum.SailAndFly].includes(
      bookingStore.getBooking.modeOfTravel)
    ) {
      const skyBusComponents = includeSkybusComponents(filteredContent);

      skyBusComponents.forEach((component) => {
        components.push(component);
      });
    }

    extrasComponentsRef.value = components.sort(function (a, b) {
      return a.order! - b.order!;
    });
  };

  const includeSkybusComponents = (content: WPContent[]): ExtrasComponent[] => {
    const contentForDangerousGoods = content.find(
      (c) => c.id === TermTypeEnum[TermTypeEnum.DangerousGoods]
    );
    const contentForTravelId = content.find(
      (c) => c.id === TermTypeEnum[TermTypeEnum.TravelId]
    );

    return [
      {
        expanded: true,
        componentName: "BookingExtraTermsCheckboxList",
        title: contentForDangerousGoods?.title ?? "",
        content: contentForDangerousGoods?.body ?? "",
        innerSections: [
          {
            resources: [
              {
                identifier: TermTypeEnum[TermTypeEnum.DangerousGoods],
                resourceCode: TermTypeEnum[TermTypeEnum.DangerousGoods],
                description: t("extras.Skybus.dangerousGoods.inputLabel"),
                disabled: false,
                type: TermTypeEnum[TermTypeEnum.DangerousGoods],
              },
            ],
          },
        ],
        icon: "vessel-aircraft",
      },
      {
        expanded: true,
        componentName: "BookingExtraTermsCheckboxList",
        title: contentForTravelId?.title ?? "",
        content: contentForTravelId?.body ?? "",
        innerSections: [
          {
            resources: [
              {
                identifier: TermTypeEnum[TermTypeEnum.TravelId],
                resourceCode: TermTypeEnum[TermTypeEnum.TravelId],
                description: t("extras.Skybus.travelId.inputLabel"),
                disabled: false,
                type: TermTypeEnum[TermTypeEnum.TravelId],
              },
            ],
          },
        ],
        icon: "vessel-aircraft",
      },
    ];
  };

  const filterSectionOnValidToDate = (content: WPContent[]): WPContent[] => {
    const outwardDate = getUnixTime(bookingStore.getDate(DirectionEnum.Outward) as Date);
    const returnDate = getUnixTime(bookingStore.getDate(DirectionEnum.Return) as Date);

    return content.map((content: WPContent) => {
      const filteredSections = content.sections.filter((section) => {
        return (
            !section.validTo ||
            (section.validTo && isAfter(section.validTo, outwardDate)) &&
            (section.validTo && isAfter(section.validTo, returnDate))
        );
      });

      return { ...content, sections: filteredSections };
    });
  };

  return { getExtras, setExtraComponentChecked };
};

export default useExtras;
